import { VStack, Heading, FormControl, FormLabel, Input, useToast, Text, InputGroup, InputRightElement, HStack, Link } from '@chakra-ui/react';
import axios from 'axios';
import SessionContext from '../contexts/SessionContext';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useState, useContext } from 'react';
import { useSubmitLogin } from '../hooks/useSubmitLogin';
import AuthLayout from '../components/auth/AuthLayout';
import { Button } from '../ui';
import PrivacyPolicy from '../modals/PrivacyPolicy';

const Login = () => {
	const sessionContext = useContext(SessionContext);
	const submitLogin = useSubmitLogin();
	const navigate = useNavigate();
	const toast = useToast();
	const [searchParams] = useSearchParams();
	const [email, setEmail] = useState(searchParams.get('email') || '');
	const [password, setPassword] = useState(searchParams.get('password') || '');
	const [showPassword, setShowPassword] = useState(false);
	const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		submitLogin.mutateAsync({ email, password })
			.then(res => {
				sessionContext?.login(res.data.sessionId);
				navigate(res.data.root);
			})
			.catch((error) => {
				const serverMessage = axios.isAxiosError(error) ? error.response?.data?.error : undefined;
				toast({
					title: serverMessage || 'Email and Password combination does not exist. Please try again',
					status: 'error',
					duration: 3000,
					isClosable: true,
				});
			});
	};

	const inputStyles = {
		bg: 'gray.50',
		borderColor: 'gray.200',
		borderRadius: 'lg',
		h: '44px',
		_hover: { borderColor: 'gray.300' },
		_focus: { borderColor: 'midnight', bg: 'white', boxShadow: 'none' },
	} as const;

	return (
		<>
			<AuthLayout
				footer={
					<HStack spacing={4} color="gray.400" fontSize="xs">
						<Link onClick={() => setIsPrivacyModalOpen(true)} _hover={{ textDecoration: 'underline' }} cursor="pointer">
							Privacy Policy
						</Link>
						<Link href="mailto:contact@yaxle.com" _hover={{ textDecoration: 'underline' }}>
							contact@yaxle.com
						</Link>
					</HStack>
				}
			>
				<VStack spacing={6} as="form" onSubmit={handleSubmit} align="stretch">
					<VStack spacing={1} align="stretch">
						<Heading fontFamily="heading" fontSize="2xl" fontWeight="bold" color="gray.900">
							Welcome back
						</Heading>
						<Text fontSize="sm" color="gray.500">
							Log in to your workspace.
						</Text>
					</VStack>

					<FormControl isRequired>
						<FormLabel fontSize="sm" color="gray.700" mb={1.5}>
							Username or email
						</FormLabel>
						<Input
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="you@shop.com"
							{...inputStyles}
						/>
					</FormControl>

					<FormControl isRequired>
						<FormLabel fontSize="sm" color="gray.700" mb={1.5}>
							Password
						</FormLabel>
						<InputGroup>
							<Input
								type={showPassword ? 'text' : 'password'}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Enter your password"
								{...inputStyles}
							/>
							<InputRightElement h="44px" w="56px">
								<Text
									as="button"
									type="button"
									fontSize="sm"
									color="gray.500"
									fontWeight="medium"
									onClick={() => setShowPassword(!showPassword)}
								>
									{showPassword ? 'Hide' : 'Show'}
								</Text>
							</InputRightElement>
						</InputGroup>
					</FormControl>

					<VStack spacing={5} align="stretch">
						<Button
							type="submit"
							w="full"
							h="44px"
							isLoading={submitLogin.isPending}
						>
							Log in
						</Button>

						<VStack spacing={2} align="stretch">
							<Text textAlign="center" fontSize="sm" color="gray.500">
								No account yet?{' '}
								<Text as={RouterLink} to="/create-account?fresh=1" color="brand.fg" fontWeight="medium" _hover={{ textDecoration: 'underline' }}>
									Create one
								</Text>
								{' '}or{' '}
								<Text as={RouterLink} to="/create-account?activation=true&fresh=1" color="brand.fg" fontWeight="medium" _hover={{ textDecoration: 'underline' }}>
									enter an activation code
								</Text>
							</Text>
							<Text
								as={RouterLink}
								to="/forgot-password"
								fontSize="sm"
								color="brand.fg"
								fontWeight="medium"
								textAlign="center"
								_hover={{ textDecoration: 'underline' }}
								tabIndex={0}
							>
								Forgot password?
							</Text>
						</VStack>
					</VStack>
				</VStack>
			</AuthLayout>
			<PrivacyPolicy isOpen={isPrivacyModalOpen} onClose={() => setIsPrivacyModalOpen(false)} />
		</>
	);
};

export default Login;
